import { useEffect, useRef } from 'react'

import { LocalStorageKey } from 'shared/constants/localStorage'
import { ShortcutGroup } from 'shared/types/desktop-grid'
import { lsGetItem, lsSetItem } from 'shared/utils/local-storage'

import { useShortcutGroupStore } from '../../../stores'

const dateReviver = (key: string, value: unknown): unknown => {
  if (key === 'createdAt' && typeof value === 'string') return new Date(value)
  return value
}

export const useShortcutGroupsPersistence = ({
  userId,
  itemIds
}: {
  userId: number | undefined
  itemIds: string[]
}) => {
  const groups = useShortcutGroupStore(state => state.groups)
  const hydrateGroups = useShortcutGroupStore(state => state.hydrateGroups)
  const clearGroups = useShortcutGroupStore(state => state.clearGroups)
  const pruneMembers = useShortcutGroupStore(state => state.pruneMembers)
  const hydratedUserIdRef = useRef<number | undefined>(undefined)
  const skipPersistRef = useRef(false)
  const itemIdsKey = itemIds.join('|')

  useEffect(() => {
    if (typeof userId !== 'number') {
      skipPersistRef.current = true
      hydratedUserIdRef.current = undefined
      clearGroups()
      return
    }

    skipPersistRef.current = true
    const saved: ShortcutGroup[] =
      lsGetItem(LocalStorageKey.DESKTOP_SHORTCUT_GROUPS, dateReviver)?.[
        userId
      ] ?? []
    const seen = new Set<string>()
    hydrateGroups(
      saved
        .filter(group => {
          if (seen.has(group.id)) return false
          seen.add(group.id)
          return true
        })
        .map(group => ({
          ...group,
          memberIds: group.memberIds
        }))
        .filter(group => group.memberIds.length > 0)
    )
    hydratedUserIdRef.current = userId
  }, [clearGroups, hydrateGroups, userId])

  useEffect(() => {
    // The desktop item store hydrates independently. Avoid pruning a freshly
    // loaded user's groups against the previous user's items (or an initial
    // empty store) before that hydration has completed.
    if (skipPersistRef.current) return
    pruneMembers(itemIdsKey ? itemIdsKey.split('|') : [])
  }, [itemIdsKey, pruneMembers])

  useEffect(() => {
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    if (typeof userId !== 'number' || hydratedUserIdRef.current !== userId) {
      return
    }
    lsSetItem(LocalStorageKey.DESKTOP_SHORTCUT_GROUPS, {
      ...(lsGetItem(LocalStorageKey.DESKTOP_SHORTCUT_GROUPS) ?? {}),
      [userId]: groups
    })
  }, [groups, userId])
}
